import { useEffect, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { downloadClientsApi } from "@/api/downloadClients";
import { indexersApi } from "@/api/indexers";
import { moviesApi } from "@/api/movies";
import { musicApi } from "@/api/music";
import { onboardingApi, type OnboardingStatus } from "@/api/onboarding";
import { settingsApi } from "@/api/settings";
import { tvApi } from "@/api/tv";
import { Button } from "@/components/ui/Button";
import { Field, Select, TextInput } from "@/components/ui/Field";
import { useToast } from "@/components/ui/Toast";
import { cn } from "@/lib/cn";

/**
 * First-run onboarding wizard (issue #26).
 *
 * Walks a fresh user through the four pieces of config that nothing
 * else can be guessed from: TMDB API key, library paths, indexer,
 * download client. Each step writes to the same per-domain endpoints
 * that Settings → * uses — the wizard is purely a UI layer over
 * those.
 *
 * Two end-states stamp `onboarding.completed_at`:
 *   1. Skip-wizard (user opts out — same end-state as completing).
 *   2. The final "Done" step.
 *
 * Once stamped, `GET /onboarding/status` returns `needs_setup=false`
 * and the dashboard never auto-opens this modal again. The Settings
 * → System "Re-run wizard" button bypasses that check by setting
 * `forceOpen` and letting the user walk through again.
 *
 * Six steps. The flow is deliberately linear (no branching) — fewer
 * branches mean the user always knows "I'm on step 4 of 6, two more
 * to go" without having to track conditional paths.
 */

interface Props {
  /**
   * When true, render regardless of `/onboarding/status`. Used by the
   * Settings → System "Re-run wizard" button so the same component
   * serves both auto-show + on-demand.
   */
  forceOpen?: boolean;
  /** Closes the wizard without stamping `completed_at`. Used for the
   *  "Re-run wizard" close button — the user explicitly opting out
   *  via Skip stamps; closing the re-run flow doesn't. */
  onClose?: () => void;
}

type StepId =
  | "welcome"
  | "tmdb"
  | "library"
  | "indexer"
  | "download"
  | "done";

const STEPS: StepId[] = [
  "welcome",
  "tmdb",
  "library",
  "indexer",
  "download",
  "done",
];

const STEP_LABELS: Record<StepId, string> = {
  welcome: "Welcome",
  tmdb: "TMDB",
  library: "Libraries",
  indexer: "Indexer",
  download: "Downloads",
  done: "Done",
};

export function OnboardingWizard({ forceOpen, onClose }: Props) {
  const status = useQuery({
    queryKey: ["onboarding", "status"],
    queryFn: onboardingApi.status,
    // Don't poll — the wizard is invoked on first load and on demand.
    refetchOnMount: true,
    staleTime: 60_000,
  });

  const open = forceOpen || (status.data?.needs_setup ?? false);

  if (status.isLoading || !open) return null;
  return <WizardModal initialStatus={status.data} onClose={onClose} />;
}

function WizardModal({
  initialStatus,
  onClose,
}: {
  initialStatus: OnboardingStatus | undefined;
  onClose?: () => void;
}) {
  const [step, setStep] = useState<StepId>("welcome");
  const qc = useQueryClient();
  const { toast } = useToast();

  // Lock body scroll while open + escape doesn't close (escape would
  // surprise a half-completed user). The user dismisses via Skip.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const completeMut = useMutation({
    mutationFn: () => onboardingApi.complete(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["onboarding", "status"] });
    },
    onError: (e) => {
      toast(
        e instanceof Error ? e.message : "Couldn't mark onboarding complete",
        "error",
      );
    },
  });

  const finish = async () => {
    await completeMut.mutateAsync();
    onClose?.();
  };

  const skip = async () => {
    // Skip-wizard MUST stamp completed_at — otherwise the modal pops up
    // again on the next page load.
    await completeMut.mutateAsync();
    toast("Onboarding skipped — you can re-run it from Settings → System.", "info");
    onClose?.();
  };

  const idx = STEPS.indexOf(step);
  const goNext = () => setStep(STEPS[Math.min(idx + 1, STEPS.length - 1)]);
  const goBack = () => setStep(STEPS[Math.max(idx - 1, 0)]);

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="First-run setup"
      className="fixed inset-0 z-50 grid place-items-center p-4"
    >
      <div
        className="absolute inset-0 animate-backdrop-in bg-black/70 backdrop-blur-md"
        aria-hidden
      />
      <div className="relative w-full max-w-2xl animate-modal-in rounded-lg border border-line bg-surface/95 shadow-elev backdrop-blur-md">
        <Header step={step} onSkip={skip} skipping={completeMut.isPending} />
        <div className="px-6 pb-6 pt-5">
          <StepBody
            step={step}
            initialStatus={initialStatus}
            onAdvance={goNext}
            onBack={goBack}
            onSkip={skip}
            onFinish={finish}
            finishing={completeMut.isPending}
          />
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ---------------------------------------------------------------------------
// Header — logo + step rail + Skip control
// ---------------------------------------------------------------------------

function Header({
  step,
  onSkip,
  skipping,
}: {
  step: StepId;
  onSkip: () => void;
  skipping: boolean;
}) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-line/60 px-6 pb-4 pt-5">
      <div className="min-w-0 flex-1">
        <div className="eyebrow mb-1.5">First-run setup</div>
        <h2 className="font-display text-xl font-semibold leading-tight tracking-display-tight">
          arr-in-one
        </h2>
        <StepRail step={step} />
      </div>
      <button
        onClick={onSkip}
        disabled={skipping}
        className="focus-ring shrink-0 text-xs text-foreground/55 underline-offset-4 hover:text-foreground hover:underline disabled:cursor-not-allowed disabled:opacity-50"
      >
        Skip wizard
      </button>
    </div>
  );
}

function StepRail({ step }: { step: StepId }) {
  const idx = STEPS.indexOf(step);
  return (
    <ol
      className="mt-4 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.14em]"
      aria-label="Wizard progress"
    >
      {STEPS.map((s, i) => {
        const active = i === idx;
        const done = i < idx;
        return (
          <li key={s} className="flex items-center gap-1.5">
            <span
              className={cn(
                "inline-flex h-5 items-center rounded px-2",
                active && "bg-accent/15 text-accent",
                done && "text-foreground/55",
                !active && !done && "text-foreground/30",
              )}
              aria-current={active ? "step" : undefined}
            >
              {STEP_LABELS[s]}
            </span>
            {i < STEPS.length - 1 && (
              <span
                aria-hidden
                className={cn(
                  "h-px w-3",
                  done ? "bg-foreground/40" : "bg-foreground/15",
                )}
              />
            )}
          </li>
        );
      })}
    </ol>
  );
}

// ---------------------------------------------------------------------------
// Step dispatch
// ---------------------------------------------------------------------------

interface StepProps {
  initialStatus: OnboardingStatus | undefined;
  onAdvance: () => void;
  onBack: () => void;
  onSkip: () => void;
  onFinish: () => void;
  finishing: boolean;
}

function StepBody({
  step,
  ...rest
}: { step: StepId } & StepProps) {
  switch (step) {
    case "welcome":
      return <WelcomeStep {...rest} />;
    case "tmdb":
      return <TmdbStep {...rest} />;
    case "library":
      return <LibraryStep {...rest} />;
    case "indexer":
      return <IndexerStep {...rest} />;
    case "download":
      return <DownloadStep {...rest} />;
    case "done":
      return <DoneStep {...rest} />;
  }
}

// ---------------------------------------------------------------------------
// Step 1 — Welcome
// ---------------------------------------------------------------------------

function WelcomeStep({ onAdvance, onSkip }: StepProps) {
  return (
    <Step
      title="Let's get you set up."
      lede="A quick four-step walk through the pieces every install needs: an API key for metadata, where to put your media, where to search for releases, and where to download them."
      footer={
        <>
          <Button variant="link" onClick={onSkip} type="button">
            Skip wizard
          </Button>
          <Button onClick={onAdvance} type="button">
            Get started →
          </Button>
        </>
      }
    >
      <ul className="space-y-2 text-sm text-foreground/70">
        <Bullet>
          <strong className="text-foreground">TMDB</strong> — free API key for
          movie + TV metadata, posters, release dates.
        </Bullet>
        <Bullet>
          <strong className="text-foreground">Library paths</strong> — where
          imports get hardlinked or moved to. All optional; set what you need.
        </Bullet>
        <Bullet>
          <strong className="text-foreground">Indexer</strong> — at least one
          Newznab/Torznab endpoint to search for releases.
        </Bullet>
        <Bullet>
          <strong className="text-foreground">Download client</strong> —
          torrent or NZB. The embedded NZB downloader is the quickest path.
        </Bullet>
      </ul>
    </Step>
  );
}

// ---------------------------------------------------------------------------
// Step 2 — TMDB API key
// ---------------------------------------------------------------------------

function TmdbStep({ onAdvance, onBack, onSkip }: StepProps) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [apiKey, setApiKey] = useState("");
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const tmdbStatus = useQuery({
    queryKey: ["settings", "tmdb"],
    queryFn: settingsApi.tmdb.get,
  });

  const alreadyConfigured = tmdbStatus.data?.configured;

  const test = async () => {
    setTesting(true);
    try {
      const res = await settingsApi.tmdb.test(apiKey.trim() || undefined);
      toast(
        res.ok
          ? "TMDB key works."
          : `Test failed: ${res.message ?? "unknown error"}`,
        res.ok ? "success" : "error",
      );
    } catch (err) {
      toast(err instanceof Error ? err.message : "Test failed", "error");
    } finally {
      setTesting(false);
    }
  };

  const saveAndAdvance = async () => {
    if (!apiKey.trim()) {
      // No key entered — only let them advance if there's already one
      // configured (DB or env). Otherwise nudge them.
      if (alreadyConfigured) {
        onAdvance();
        return;
      }
      toast("Enter a TMDB key, or click Skip to add one later.", "info");
      return;
    }
    setSaving(true);
    try {
      await settingsApi.tmdb.set(apiKey.trim());
      qc.invalidateQueries({ queryKey: ["settings", "tmdb"] });
      toast("TMDB key saved.", "success");
      onAdvance();
    } catch (err) {
      toast(err instanceof Error ? err.message : "Save failed", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Step
      title="TMDB API key."
      lede="The Movie Database powers movie and TV metadata. The free tier is plenty for personal libraries."
      footer={
        <>
          <BackButton onBack={onBack} />
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onSkip} type="button">
              Skip wizard
            </Button>
            <Button variant="secondary" onClick={onAdvance} type="button">
              Skip step
            </Button>
            <Button onClick={saveAndAdvance} loading={saving} type="button">
              {alreadyConfigured && !apiKey.trim() ? "Continue" : "Save & continue"}
            </Button>
          </div>
        </>
      }
    >
      {alreadyConfigured && (
        <p className="rounded border border-line bg-surface/40 px-3 py-2 text-xs text-foreground/65">
          A TMDB key is already configured (
          <code className="font-mono">{tmdbStatus.data?.source}</code>
          ). Leave the field blank to keep it.
        </p>
      )}
      <Field
        label="API key"
        hint={
          <>
            Get one at{" "}
            <a
              href="https://www.themoviedb.org/settings/api"
              target="_blank"
              rel="noopener noreferrer"
              className="text-accent underline-offset-4 hover:underline"
            >
              themoviedb.org → Settings → API
            </a>
            .
          </>
        }
      >
        <TextInput
          type="password"
          autoComplete="off"
          value={apiKey}
          placeholder={alreadyConfigured ? "leave blank to keep current" : "v3 API key"}
          onChange={(e) => setApiKey(e.target.value)}
        />
      </Field>
      <Button
        type="button"
        variant="secondary"
        size="sm"
        onClick={test}
        loading={testing}
      >
        Test key
      </Button>
    </Step>
  );
}

// ---------------------------------------------------------------------------
// Step 3 — Library paths
// ---------------------------------------------------------------------------

function LibraryStep({ onAdvance, onBack, onSkip }: StepProps) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const movies = useQuery({
    queryKey: ["movies", "settings"],
    queryFn: moviesApi.settings.get,
  });
  const tv = useQuery({
    queryKey: ["tv", "settings"],
    queryFn: tvApi.settings.get,
  });
  const music = useQuery({
    queryKey: ["music", "settings"],
    queryFn: musicApi.settings.get,
  });

  const [moviesPath, setMoviesPath] = useState("");
  const [tvPath, setTvPath] = useState("");
  const [musicPath, setMusicPath] = useState("");
  const [saving, setSaving] = useState(false);

  // Pre-fill once the GETs land — without overwriting subsequent edits.
  useEffect(() => {
    if (movies.data?.library_path && moviesPath === "") {
      setMoviesPath(movies.data.library_path);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [movies.data?.library_path]);
  useEffect(() => {
    if (tv.data?.library_path && tvPath === "") setTvPath(tv.data.library_path);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tv.data?.library_path]);
  useEffect(() => {
    if (music.data?.library_path && musicPath === "") {
      setMusicPath(music.data.library_path);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [music.data?.library_path]);

  const saveAndAdvance = async () => {
    setSaving(true);
    try {
      const writes: Promise<unknown>[] = [];
      const movies_v = moviesPath.trim();
      const tv_v = tvPath.trim();
      const music_v = musicPath.trim();
      if (movies_v && movies_v !== (movies.data?.library_path ?? "")) {
        writes.push(moviesApi.settings.update({ library_path: movies_v }));
      }
      if (tv_v && tv_v !== (tv.data?.library_path ?? "")) {
        writes.push(tvApi.settings.update({ library_path: tv_v }));
      }
      if (music_v && music_v !== (music.data?.library_path ?? "")) {
        writes.push(musicApi.settings.update({ library_path: music_v }));
      }
      await Promise.all(writes);
      qc.invalidateQueries({ queryKey: ["movies", "settings"] });
      qc.invalidateQueries({ queryKey: ["tv", "settings"] });
      qc.invalidateQueries({ queryKey: ["music", "settings"] });
      qc.invalidateQueries({ queryKey: ["onboarding", "status"] });
      onAdvance();
    } catch (err) {
      // The settings update endpoints validate exists+writable on the
      // backend (see /settings/movies handler) and return a 400 with a
      // human message — surface that.
      toast(err instanceof Error ? err.message : "Save failed", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Step
      title="Library paths."
      lede="Absolute paths the importer renames or hardlinks completed downloads into. All optional — leave blank for kinds you don't manage. The server validates each path exists and is writable before saving."
      footer={
        <>
          <BackButton onBack={onBack} />
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onSkip} type="button">
              Skip wizard
            </Button>
            <Button variant="secondary" onClick={onAdvance} type="button">
              Skip step
            </Button>
            <Button onClick={saveAndAdvance} loading={saving} type="button">
              Save & continue
            </Button>
          </div>
        </>
      }
    >
      <Field label="Movies" hint="Example: /var/media/movies">
        <TextInput
          value={moviesPath}
          placeholder="/var/media/movies"
          onChange={(e) => setMoviesPath(e.target.value)}
        />
      </Field>
      <Field label="TV" hint="Example: /var/media/tv">
        <TextInput
          value={tvPath}
          placeholder="/var/media/tv"
          onChange={(e) => setTvPath(e.target.value)}
        />
      </Field>
      <Field label="Music" hint="Example: /var/media/music">
        <TextInput
          value={musicPath}
          placeholder="/var/media/music"
          onChange={(e) => setMusicPath(e.target.value)}
        />
      </Field>
    </Step>
  );
}

// ---------------------------------------------------------------------------
// Step 4 — Indexer
// ---------------------------------------------------------------------------

function IndexerStep({ onAdvance, onBack, onSkip }: StepProps) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const list = useQuery({
    queryKey: ["indexers"],
    queryFn: indexersApi.list,
  });
  const [name, setName] = useState("");
  const [baseUrl, setBaseUrl] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [protocol, setProtocol] = useState<"usenet" | "torrent">("usenet");
  const [saving, setSaving] = useState(false);

  const hasAny = (list.data?.length ?? 0) > 0;

  const create = async () => {
    if (!name.trim() || !baseUrl.trim()) {
      toast("Name and base URL are required.", "error");
      return;
    }
    setSaving(true);
    try {
      await indexersApi.create({
        name: name.trim(),
        base_url: baseUrl.trim(),
        api_key: apiKey.trim() || null,
        protocol,
      });
      qc.invalidateQueries({ queryKey: ["indexers"] });
      qc.invalidateQueries({ queryKey: ["onboarding", "status"] });
      toast("Indexer added.", "success");
      onAdvance();
    } catch (err) {
      toast(err instanceof Error ? err.message : "Couldn't add indexer", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Step
      title="Indexer."
      lede="Where releases get searched. Newznab for usenet, Torznab for torrents — most providers expose both."
      footer={
        <>
          <BackButton onBack={onBack} />
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onSkip} type="button">
              Skip wizard
            </Button>
            <Button variant="secondary" onClick={onAdvance} type="button">
              {hasAny ? "Continue" : "Skip & add later"}
            </Button>
            <Button onClick={create} loading={saving} type="button">
              Add & continue
            </Button>
          </div>
        </>
      }
    >
      {hasAny && (
        <p className="rounded border border-line bg-surface/40 px-3 py-2 text-xs text-foreground/65">
          {list.data?.length} indexer
          {(list.data?.length ?? 0) === 1 ? "" : "s"} already configured. Add
          another below or click Continue.
        </p>
      )}
      <Field label="Name" hint="Free-form label, e.g. NZBGeek.">
        <TextInput
          value={name}
          placeholder="NZBGeek"
          onChange={(e) => setName(e.target.value)}
        />
      </Field>
      <Field label="Base URL" hint="The full Newznab/Torznab base URL.">
        <TextInput
          value={baseUrl}
          placeholder="https://api.example.com"
          onChange={(e) => setBaseUrl(e.target.value)}
        />
      </Field>
      <Field label="API key" hint="From your indexer's profile page.">
        <TextInput
          type="password"
          autoComplete="off"
          value={apiKey}
          onChange={(e) => setApiKey(e.target.value)}
        />
      </Field>
      <Field label="Protocol" hint="Most usenet providers use Newznab; trackers expose Torznab.">
        <Select
          value={protocol}
          onChange={(e) => setProtocol(e.target.value as "usenet" | "torrent")}
        >
          <option value="usenet">Usenet (Newznab)</option>
          <option value="torrent">Torrent (Torznab)</option>
        </Select>
      </Field>
    </Step>
  );
}

// ---------------------------------------------------------------------------
// Step 5 — Download client
// ---------------------------------------------------------------------------

type ClientChoice = "embedded" | "qbittorrent" | "sabnzbd" | "nzbget";

function DownloadStep({ onAdvance, onBack, onSkip }: StepProps) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const list = useQuery({
    queryKey: ["download-clients"],
    queryFn: downloadClientsApi.list,
  });

  // Pre-select embedded since it's the zero-config quickstart and is
  // already seeded by migration 0014.
  const [choice, setChoice] = useState<ClientChoice>("embedded");
  const [name, setName] = useState("");
  const [host, setHost] = useState("localhost");
  const [port, setPort] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [saving, setSaving] = useState(false);

  const embeddedRow = list.data?.find((r) => r.kind === "embedded");

  // Sensible default ports per kind so the user doesn't have to remember.
  useEffect(() => {
    if (choice === "qbittorrent") setPort("8080");
    else if (choice === "sabnzbd") setPort("8080");
    else if (choice === "nzbget") setPort("6789");
    else setPort("");
  }, [choice]);

  const continueWithEmbedded = async () => {
    setSaving(true);
    try {
      // Embedded is auto-seeded; if it's enabled, we're done. If it's
      // somehow disabled, flip it back on so the gate clears.
      if (embeddedRow && !embeddedRow.enabled) {
        await downloadClientsApi.update(embeddedRow.id, { enabled: true });
        qc.invalidateQueries({ queryKey: ["download-clients"] });
      }
      qc.invalidateQueries({ queryKey: ["onboarding", "status"] });
      onAdvance();
    } catch (err) {
      toast(err instanceof Error ? err.message : "Couldn't enable embedded", "error");
    } finally {
      setSaving(false);
    }
  };

  const create = async () => {
    if (!name.trim() || !host.trim() || !port.trim()) {
      toast("Name, host, and port are required.", "error");
      return;
    }
    const portNum = Number(port);
    if (!Number.isInteger(portNum) || portNum <= 0 || portNum > 65535) {
      toast("Port must be 1–65535.", "error");
      return;
    }
    setSaving(true);
    try {
      const protocol: "torrent" | "usenet" = choice === "qbittorrent" ? "torrent" : "usenet";
      await downloadClientsApi.create({
        name: name.trim(),
        kind: choice as "qbittorrent" | "sabnzbd" | "nzbget",
        protocol,
        host: host.trim(),
        port: portNum,
        username: username.trim() || null,
        password: password || null,
        api_key: apiKey.trim() || null,
      });
      qc.invalidateQueries({ queryKey: ["download-clients"] });
      qc.invalidateQueries({ queryKey: ["onboarding", "status"] });
      toast("Download client added.", "success");
      onAdvance();
    } catch (err) {
      toast(err instanceof Error ? err.message : "Couldn't add client", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Step
      title="Download client."
      lede="Where releases land before import. The embedded NZB downloader is the fastest setup — no extra container, no port-forward."
      footer={
        <>
          <BackButton onBack={onBack} />
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onSkip} type="button">
              Skip wizard
            </Button>
            <Button variant="secondary" onClick={onAdvance} type="button">
              Skip step
            </Button>
            {choice === "embedded" ? (
              <Button onClick={continueWithEmbedded} loading={saving} type="button">
                Use embedded & continue
              </Button>
            ) : (
              <Button onClick={create} loading={saving} type="button">
                Add & continue
              </Button>
            )}
          </div>
        </>
      }
    >
      <Field label="Type">
        <Select
          value={choice}
          onChange={(e) => setChoice(e.target.value as ClientChoice)}
        >
          <option value="embedded">Embedded NZB downloader (recommended)</option>
          <option value="qbittorrent">qBittorrent</option>
          <option value="sabnzbd">SABnzbd</option>
          <option value="nzbget">NZBGet</option>
        </Select>
      </Field>

      {choice === "embedded" ? (
        <p className="rounded border border-line bg-surface/40 px-3 py-2 text-xs text-foreground/65">
          The built-in usenet downloader handles NZBs end-to-end inside
          arr-in-one. It's already seeded — clicking continue just confirms
          you're using it. You can configure servers later under
          Settings → NZB.
        </p>
      ) : (
        <>
          <Field label="Name">
            <TextInput
              value={name}
              placeholder={`My ${choice}`}
              onChange={(e) => setName(e.target.value)}
            />
          </Field>
          <div className="grid grid-cols-[1fr_8rem] gap-3">
            <Field label="Host">
              <TextInput
                value={host}
                onChange={(e) => setHost(e.target.value)}
              />
            </Field>
            <Field label="Port">
              <TextInput
                inputMode="numeric"
                value={port}
                onChange={(e) => setPort(e.target.value)}
              />
            </Field>
          </div>
          {choice === "qbittorrent" ? (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Username">
                <TextInput
                  value={username}
                  autoComplete="off"
                  onChange={(e) => setUsername(e.target.value)}
                />
              </Field>
              <Field label="Password">
                <TextInput
                  type="password"
                  autoComplete="off"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </Field>
            </div>
          ) : (
            <Field label="API key">
              <TextInput
                type="password"
                autoComplete="off"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
              />
            </Field>
          )}
        </>
      )}
    </Step>
  );
}

// ---------------------------------------------------------------------------
// Step 6 — Done
// ---------------------------------------------------------------------------

function DoneStep({ onBack, onFinish, finishing }: StepProps) {
  const summary = useQuery({
    queryKey: ["onboarding", "status"],
    queryFn: onboardingApi.status,
  });

  return (
    <Step
      title="You're all set."
      lede="The pieces below are configured. Anything still missing can be added from Settings — and you can re-run this wizard from Settings → System anytime."
      footer={
        <>
          <BackButton onBack={onBack} />
          <Button onClick={onFinish} loading={finishing} type="button">
            Take me to the dashboard →
          </Button>
        </>
      }
    >
      <SummaryList
        rows={[
          {
            id: "tmdb_key",
            label: "TMDB API key",
          },
          {
            id: "library_paths",
            label: "Library paths",
          },
          {
            id: "indexer",
            label: "Indexer",
          },
          {
            id: "download_client",
            label: "Download client",
          },
        ]}
        missing={summary.data?.missing_steps ?? []}
      />
    </Step>
  );
}

function SummaryList({
  rows,
  missing,
}: {
  rows: { id: string; label: string }[];
  missing: string[];
}) {
  return (
    <ul className="divide-y divide-line/50 rounded border border-line bg-surface/30 text-sm">
      {rows.map((r) => {
        const ok = !missing.includes(r.id);
        return (
          <li
            key={r.id}
            className="flex items-center justify-between gap-2 px-4 py-2.5"
          >
            <span className="text-foreground/85">{r.label}</span>
            <span
              className={cn(
                "rounded-full px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.14em]",
                ok
                  ? "bg-good/15 text-good"
                  : "bg-warn/15 text-warn",
              )}
            >
              {ok ? "configured" : "still needed"}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

// ---------------------------------------------------------------------------
// Step shell + helpers
// ---------------------------------------------------------------------------

function Step({
  title,
  lede,
  children,
  footer,
}: {
  title: string;
  lede?: string;
  children?: ReactNode;
  footer: ReactNode;
}) {
  return (
    <div className="space-y-5">
      <div>
        <h3 className="font-display text-2xl font-semibold leading-tight tracking-display-tight">
          {title}
        </h3>
        {lede && (
          <p className="mt-2 text-sm leading-relaxed text-foreground/65">
            {lede}
          </p>
        )}
      </div>
      <div className="space-y-4">{children}</div>
      <div className="flex items-center justify-between gap-3 border-t border-line/60 pt-4">
        {footer}
      </div>
    </div>
  );
}

function BackButton({ onBack }: { onBack: () => void }) {
  return (
    <Button variant="link" onClick={onBack} type="button">
      ← Back
    </Button>
  );
}

function Bullet({ children }: { children: ReactNode }) {
  return (
    <li className="flex gap-3">
      <span
        aria-hidden
        className="mt-1.5 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-accent/70"
      />
      <span>{children}</span>
    </li>
  );
}
